import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import SvgFacebook from '@/src/assets/svgs/social/facebook-circle.svg'
import SvgGoogle from '@/src/assets/svgs/social/google.svg'
import { signIn } from 'next-auth/react'
import { setCookie } from 'cookies-next'
import PathHelper from '@/src/helpers/path-helper'
import { CookiesSocialLogin } from '@/src/constants/cookie-names'
import { useMerchant } from '@/src/context/merchant/merchant-context'
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation'
import { useAppAccessToken } from '@/src/context/app-access-token/app-access-token-context'
import Merchant from '@/src/interfaces/merchant/merchant'
import LoadingFullscreen from '@/src/components/loadings/loading-fullscreen/loading-fullscreen'
import LoadingDots from '@/src/components/loadings/loading-dots/loading-dots'

interface LoginOption {
    name: string
    svg: JSX.Element
    onClick(): void
}

interface Props {
    isSendingOtp: boolean
    handleSubmit(e: React.FormEvent, isOtp: boolean): void
    canTryToLogin: boolean
}

export interface SocialMerchantData {
    id: string
    subdomain: string
    redirect?: string | null
}

export const signInSocial = async (
    source: 'google' | 'facebook',
    merchantSummary: Merchant.ResponseSummary,
    searchParams: ReadonlyURLSearchParams,
    appAccessToken: string,
    skipSelfdomain: boolean = false,
    selfdomain?: string,
    redirectPath?: string
) => {
    let host = PathHelper.getHostname(window.location.hostname)
    const redirect = redirectPath ?? searchParams.get('redirect')

    const [firstHost, secondHost] = host.split('.')
    const isSelfDomain = firstHost !== 'menudino' && secondHost !== 'menudino'

    if (isSelfDomain && !skipSelfdomain) {
        const url = new URL(window.location.href)
        const selfdomain = encodeURIComponent(`${url.protocol}//${url.host}`)
        window.location.replace(`https://${merchantSummary?.url}.menudino.com/selfdomain?provider=${source}&selfdomain=${selfdomain}${redirect ? `&redirect=${redirect}` : ''}`)
        return
    }

    setCookie(CookiesSocialLogin.TempAppAccessToken, appAccessToken, {
        domain: `.${host}`,
        path: '/',
    })

    const data: SocialMerchantData = {
        id: merchantSummary.id,
        subdomain: selfdomain ?? PathHelper.getDomain(),
        redirect: redirect
    }

    setCookie(CookiesSocialLogin.MerchantData, JSON.stringify(data), {
        domain: `.${host}`,
        path: '/',
    })

    await signIn(source, { redirect: true })
}

export default function AlternativeLoginMethods(props: Props) {
    const { merchantSummary, isAppAndroidOrIOS } = useMerchant()
    const searchParams = useSearchParams()
    const { appAccessToken } = useAppAccessToken()
    const [isLoading, setIsLoading] = useState(false)

    const loginOptions: LoginOption[] = [
        {
            name: "Continuar com Google",
            svg: <SvgGoogle />,
            onClick: async () => {
                try {
                    setIsLoading(true)
                    await signInSocial('google', merchantSummary!, searchParams, appAccessToken!)
                }
                catch (e) {
                    console.error(e)
                }
                finally {
                    setIsLoading(false)
                }
            }
        },
        {
            name: "Continuar com Facebook",
            svg: <SvgFacebook />,
            onClick: async () => {
                try {
                    setIsLoading(true)
                    await signInSocial('facebook', merchantSummary!, searchParams, appAccessToken!)
                }
                catch (e) {
                    console.error(e)
                }
                finally {
                    setIsLoading(false)
                }
            }
        }
    ]
    return (
        <>
            {isLoading && <LoadingFullscreen defaultSpinner />}
            <div className={styles.container}>
                {
                    !isAppAndroidOrIOS &&
                    <div className={styles.socialLoginAreaTitle}>
                        <div />
                        <span>OU</span>
                        <div />
                    </div>
                }
                {
                    props.canTryToLogin ?
                        <div className={styles.otpArea}>
                            <span className='font-sm'>Entrar sem senha</span>
                            <div onClick={(e) => !props.isSendingOtp && props.handleSubmit(e, true)} className={`${styles.btnOtp} ${props.isSendingOtp ? styles.sendingOtp : ''}`}>
                                {props.isSendingOtp ? <LoadingDots /> : <label>Receber código por e-mail</label>}
                            </div>
                        </div>
                        :
                        !isAppAndroidOrIOS ?
                            <div className={styles.loginOptions}>
                                {
                                    loginOptions.map((item, index) => (
                                        <button type='button' onClick={item.onClick} key={index} className={styles.loginOption}>
                                            <div className={styles.socialLogoArea}>
                                                {item.svg}
                                            </div>
                                            <span>{item.name}</span>
                                        </button>
                                    ))
                                }
                            </div>
                            : <></>
                }
            </div>
        </>
    )
}
